/**
 * AdminGuard — client-side admin route gate.
 *
 * Calls `admin.ping` on mount via the shared rpcPost helper (which attaches
 * the CSRF token). On success (admin session confirmed), renders children.
 * On 403 (not signed in / not admin), redirects to `/`.
 * On 5xx (ADMIN_EMAIL misconfigured on the Worker), renders a diagnostic notice.
 *
 * This is an SPA-mode gate — there is no SSR loader. The ping is a lightweight
 * oRPC call that returns { ok: true, adminEmail } on success (admin.ping spec).
 *
 * Architecture refs:
 *   feature plan §4.6 — admin auth model
 *   CLAUDE.md §3 — /admin/* routes excluded from precache (Workbox)
 *
 * Note: "admin" and "ping" are internal procedure identifiers. The user-facing
 * admin panel never exposes these strings.
 */

import { useState, useEffect } from "react";
import { Navigate } from "react-router";
import { rpcPost, AdminRpcError } from "./api";

interface AdminGuardProps {
  children: React.ReactNode;
}

type GuardState = "loading" | "allowed" | "denied" | "error";

async function checkAdminSession(): Promise<{ allowed: boolean; configError: boolean }> {
  try {
    const result = await rpcPost<{ ok: boolean; adminEmail: string }>("admin.ping", {});
    return { allowed: result.ok === true, configError: false };
  } catch (err) {
    // A 500 from admin.ping means ADMIN_EMAIL is not configured on the server —
    // treat as a misconfiguration (not an auth denial) so the operator can
    // distinguish a setup problem from a genuine access-denied case.
    if (err instanceof AdminRpcError && err.status >= 500) {
      console.error(
        "[AdminGuard] admin.ping returned a server error — ADMIN_EMAIL may not be set on the Worker.",
        err,
      );
      return { allowed: false, configError: true };
    }
    // 401 / 403 — not signed in or not an admin.
    return { allowed: false, configError: false };
  }
}

export function AdminGuard({ children }: AdminGuardProps) {
  const [state, setState] = useState<GuardState>("loading");

  useEffect(() => {
    let cancelled = false;
    checkAdminSession()
      .then(({ allowed, configError }) => {
        if (!cancelled) setState(allowed ? "allowed" : configError ? "error" : "denied");
      })
      .catch((err: unknown) => {
        // Unexpected throw (network failure, parse error, etc.) — log and deny.
        console.error("[AdminGuard] Unexpected error during session check:", err);
        if (!cancelled) setState("denied");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (state === "loading") {
    // Render nothing during the check — prevents flash of admin content.
    return null;
  }

  if (state === "error") {
    // Server-side misconfiguration (ADMIN_EMAIL unset) — show a diagnostic
    // rather than silently redirecting so the operator knows why access fails.
    return (
      <div
        style={{
          padding: "2rem",
          fontFamily: "var(--font-sans, system-ui, sans-serif)",
          fontSize: "0.875rem",
          color: "var(--text-primary, #111)",
        }}
        role="alert"
      >
        <p>Admin access is not available.</p>
        <p style={{ color: "var(--text-muted, #6b7280)", marginTop: "0.5rem" }}>
          The server returned an error while checking credentials. Confirm that{" "}
          <code>ADMIN_EMAIL</code> is set on the Worker and that you are signed in with a matching
          account.
        </p>
      </div>
    );
  }

  if (state === "denied") {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

export default AdminGuard;
